import { BodyType, Series } from "../../entities/content";
import {
  determineMainDrawerLevel,
  getLastRoutingSegment,
  getPreviousRoutingKey,
} from "../../entities/routing-key";
import {
  DEALER_SEARCH_ELEMENT_TYPE,
  DisplayElementType,
  ElementType,
  ElementTypes,
} from "../../utility/constants/analytics";
import { isString } from "../../utility/helper";
import { getCurrentRoute } from "../routing-manager";
import { getSideDrawerRoutingKey } from "../menu-items-service";

const state: {
  drawerType?: DisplayElementType;
  elementType?: ElementType;
} = {};

function setDrawerType(drawerType: DisplayElementType): void {
  state.drawerType = drawerType;
}

function calculateDisplayElementId(
  routingKeyForNavigatingForward?: string,
): string {
  const { drawerType } = state;
  const mainDrawerRoutingKey =
    (isString(routingKeyForNavigatingForward)
      ? getPreviousRoutingKey(routingKeyForNavigatingForward)
      : getCurrentRoute(drawerType === "doubleDrawer")) || "";
  const lastMainDrawerRoutingKey = getLastRoutingSegment(mainDrawerRoutingKey);
  const mainDrawerLevel = determineMainDrawerLevel(mainDrawerRoutingKey);
  const sideDrawerLevel = mainDrawerLevel - 1;

  if (!isString(mainDrawerRoutingKey)) {
    return "level0";
  }

  const mainDrawerDisplayElementId = isString(mainDrawerRoutingKey)
    ? `level${mainDrawerLevel}-${lastMainDrawerRoutingKey}`
    : `level${mainDrawerLevel - 1}-unknown`;

  if (drawerType === "doubleDrawer") {
    if (mainDrawerRoutingKey === "") {
      return `level${sideDrawerLevel}-unknown_level${mainDrawerLevel}-unknown`;
    }
    const sideDrawerRoutingKey = getSideDrawerRoutingKey(mainDrawerRoutingKey);
    return `level${sideDrawerLevel}-${sideDrawerRoutingKey}_${mainDrawerDisplayElementId}`;
  }

  return mainDrawerDisplayElementId;
}

function calculateDisplayElementType(
  activeRoutingKey: string,
  elementType: ElementType,
  isSideDrawer?: boolean,
): string {
  const { drawerType } = state;
  const mainDrawerLevel = determineMainDrawerLevel(activeRoutingKey);

  if (drawerType === "doubleDrawer") {
    let sideDrawerLevel = mainDrawerLevel;
    if (isSideDrawer) {
      sideDrawerLevel -= 1;
    }
    return `level${sideDrawerLevel}_${elementType}`;
  }
  return isString(activeRoutingKey)
    ? `level${mainDrawerLevel}_${elementType}`
    : `level${mainDrawerLevel - 1}_${elementType}`;
}

function getModelName(bodyType: BodyType): string {
  const urlSplitIntoPieces: string[] = bodyType.link.split("/");
  const lastElementInUrlSplit: string =
    urlSplitIntoPieces[urlSplitIntoPieces.length - 1];
  let modelName: string;

  if (lastElementInUrlSplit === "") {
    if (urlSplitIntoPieces[urlSplitIntoPieces.length - 2] === undefined) {
      modelName = "";
    } else {
      modelName = urlSplitIntoPieces[urlSplitIntoPieces.length - 2]
        .split("-")
        .join(" ");
    }
  } else {
    modelName = lastElementInUrlSplit.split("-").join(" ");
  }

  return modelName;
}

function getEngineTypes(model: BodyType | Series): string[] {
  if ("bodyTypes" in model) {
    const engineTypes = model.bodyTypes.flatMap(getEngineTypes);
    return [...new Set(engineTypes)];
  }
  return model.tags
    .filter((tag) => tag.engineType)
    .map((tag) => tag.engineType as string);
}

function getModelTargetElement(
  elementId: string,
  text: string,
): string | undefined {
  if (elementId.includes("models")) {
    const mainDrawerLevel = determineMainDrawerLevel(elementId);

    return `level${mainDrawerLevel}_${text.toLowerCase()}`;
  }

  return undefined;
}

function getDrawerType(): DisplayElementType | undefined {
  return state.drawerType;
}

function getDealerResultsLoadDisplayElementId(elementId: string): string {
  if (getDrawerType() === "doubleDrawer") {
    return elementId;
  }
  return calculateDisplayElementId();
}

function getDealerResultsLoadDisplayElementType(): string {
  if (getDrawerType() === "doubleDrawer") {
    return DEALER_SEARCH_ELEMENT_TYPE;
  }
  return calculateDisplayElementType("", ElementTypes.dealerSearchForm);
}

export default {
  setDrawerType,
  getDealerResultsLoadDisplayElementId,
  calculateDisplayElementId,
  getDealerResultsLoadDisplayElementType,
  calculateDisplayElementType,
  getModelName,
  getModelTargetElement,
  getEngineTypes,
};
